package one.healthium.uscreen.navigation

import androidx.compose.animation.AnimatedVisibility
import one.healthium.uscreen.ui.animation.TRANSITION_EASING
import androidx.compose.animation.core.tween
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
import com.arkivanov.decompose.Child
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import one.healthium.uscreen.navigation.permissions.rememberNotificationPermissionRequester
import one.healthium.uscreen.domain.models.CtaAction
import one.healthium.uscreen.domain.models.StatusBarStyle
import one.primehealth.analytics.permissions.TrackingPermission
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.UScreenCallbacks
import one.healthium.uscreen.ui.UScreenViewModel
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.primehealth.analytics.compose.AppEventEffect
import one.primehealth.analytics.events.Events
import one.healthium.uscreen.ui.animation.LocalActiveScreenId
import one.healthium.uscreen.ui.animation.LocalNavigatingBack
import one.healthium.uscreen.ui.animation.PROGRESS_BAR_ANIM_DURATION
import one.healthium.uscreen.ui.components.UScreenProgressBar
import one.healthium.uscreen.ui.layout.LocalProgressBarHeight
import one.healthium.uscreen.ui.shell.UScreenShell
import org.koin.compose.viewmodel.koinViewModel

/**
 * Generic Navigation with per-block enter/exit and pre-warming.
 * Uses [DefaultUScreenComponent] which manages screen lifecycle via Decompose's `children()` API.
 * - RESUMED screens: active, blocks play inAnim
 * - STARTED screens: exiting, blocks play outAnim
 * - CREATED screens: pre-warmed (composed but not yet visible)
 */
@Composable
fun UScreenContent(
    component: UScreenComponent,
    modifier: Modifier = Modifier,
) {
    val childState by component.screenChildren.subscribeAsState()
    val activeChild = childState.items
        .filterIsInstance<Child.Created<*, UScreenComponent.UScreenChild>>()
        .first { it.instance.screenIndex == childState.activeIndex }
        .instance

    // Direction detection
    var prevActiveIndex by remember { mutableStateOf(childState.activeIndex) }
    var isNavigatingBack by remember { mutableStateOf(false) }
    if (childState.activeIndex != prevActiveIndex) {
        isNavigatingBack = childState.activeIndex < prevActiveIndex
        prevActiveIndex = childState.activeIndex
    }

    val requestNotificationPermission = rememberNotificationPermissionRequester()
    val viewModel = koinViewModel<UScreenViewModel>()
    viewModel.setFlowModel(component.model)
    val answers by viewModel.answers.collectAsState()
    val placeholders by viewModel.placeholders.collectAsState()
    val isMale = (answers["gender"] as? UScreenAnswer.ChoiceAnswer)
        ?.selectedKeys?.firstOrNull()?.let { it == "1" }

    val keyboardController = LocalSoftwareKeyboardController.current
    val density = LocalDensity.current
    val scope = rememberCoroutineScope()
    val isKeyboardVisible = WindowInsets.ime.getBottom(density) > 0

    PlatformColorsEffect(lightStatusBar = activeChild.screen.statusBar == StatusBarStyle.Light)

    val screenId = activeChild.screen.id
    AppEventEffect(screenId) {
        logEvent(
            Events.OnboardingScreenViewed(
                screenName = screenId,
                onboardingVariant = "${component.model.flow}_v${component.model.version}",
            )
        )
    }

    // CTA — no dynamic CTA on single-select screens, no preselect
    val cta = activeChild.screen.cta
    val reserveCtaSpace = cta != null
    val ctaEnabled = cta?.requiresKey?.let { key ->
        viewModel.isKeyFilled(key, answers)
    } ?: true

    fun navigateNext() {
        viewModel.persistAnswers()
        component.navigateNext()
    }

    fun dismissKeyboardThen(action: () -> Unit) {
        if (isKeyboardVisible) {
            keyboardController?.hide()
            scope.launch {
                delay(300)
                action()
            }
        } else {
            action()
        }
    }

    fun dispatchAction(action: CtaAction) {
        when (action) {
            CtaAction.Back -> component.onBackClicked()
            CtaAction.SignIn -> component.onSignIn()
            CtaAction.Restore -> component.onRestore()
            CtaAction.Purchase -> component.onPurchase()
            is CtaAction.OpenUrl -> component.onOpenUrl(action.url)
            is CtaAction.GoToScreen -> component.onGoToScreen(action.screen)
            CtaAction.RequestTracking -> {
                scope.launch {
                    TrackingPermission.requestTrackingAuthorization()
                    component.navigateNext()
                }
            }
            CtaAction.RequestNotifications -> {
                scope.launch {
                    requestNotificationPermission()
                    component.navigateNext()
                }
            }
            else -> component.navigateNext()
        }
    }

    fun handleCtaAction() {
        viewModel.persistAnswers()
        dispatchAction(cta?.action ?: CtaAction.Next)
    }

    val callbacks = object : UScreenCallbacks {
        override fun onChoiceSelected(key: String, optionKey: String, single: Boolean) =
            viewModel.onChoiceSelected(key, optionKey, single)
        override fun onTextChanged(key: String, value: String) =
            viewModel.onTextChanged(key, value)
        override fun onNumberChanged(key: String, value: Float) =
            viewModel.onNumberChanged(key, value)
        override fun onAutoAdvance() = navigateNext()
        override fun onKeyboardSubmit() {
            if (ctaEnabled) dismissKeyboardThen(::navigateNext)
        }
        override fun onButtonAction(action: CtaAction) {
            viewModel.persistAnswers()
            dispatchAction(action)
        }
    }

    // No-op callbacks for exiting screens
    val noOpCallbacks = object : UScreenCallbacks {
        override fun onChoiceSelected(key: String, optionKey: String, single: Boolean) {}
        override fun onTextChanged(key: String, value: String) {}
        override fun onNumberChanged(key: String, value: Float) {}
        override fun onAutoAdvance() {}
        override fun onKeyboardSubmit() {}
        override fun onButtonAction(action: CtaAction) {}
    }

    var progressBarHeightDp by remember { mutableStateOf(0.dp) }

    CompositionLocalProvider(
        LocalViewModelStoreOwner provides component,
        LocalProgressBarHeight provides progressBarHeightDp,
        LocalNavigatingBack provides isNavigatingBack,
        LocalActiveScreenId provides activeChild.screen.id,
    ) {
        Box(modifier = modifier.fillMaxSize().imePadding().testTag("onboarding")) {
            // Render children by lifecycle status
            // STARTED (exiting) rendered first (behind), then RESUMED (active) on top
            childState.items
                .filterIsInstance<Child.Created<DefaultUScreenComponent.ScreenConfig, UScreenComponent.UScreenChild>>()
                .sortedBy { child ->
                    // Exiting first (behind), active last (on top)
                    when {
                        child.instance.screenIndex == childState.previousIndex -> 0
                        child.instance.screenIndex == childState.activeIndex -> 1
                        else -> -1 // pre-warmed: don't render visually
                    }
                }
                .forEach { child ->
                    val isActive = child.instance.screenIndex == childState.activeIndex
                    val isExiting = child.instance.screenIndex == childState.previousIndex
                    val isPreWarmed = !isActive && !isExiting

                    if (isPreWarmed) return@forEach

                    key(child.instance.screenIndex) {
                        val screenCta = if (isActive) cta else child.instance.screen.cta
                        UScreenShell(
                            screen = child.instance.screen,
                            cta = screenCta,
                            reserveCtaSpace = if (isActive) reserveCtaSpace else screenCta != null,
                            ctaEnabled = if (isActive) ctaEnabled else false,
                            onCtaClick = if (isActive) {{ dismissKeyboardThen(::handleCtaAction) }} else {{}},
                            answers = answers,
                            isMale = isMale,
                            placeholders = placeholders,
                            callbacks = if (isActive) callbacks else noOpCallbacks,
                        )
                    }
                }

            // Progress bar overlay
            AnimatedVisibility(
                visible = activeChild.screen.progressBar,
                enter = slideInHorizontally(
                    animationSpec = tween(PROGRESS_BAR_ANIM_DURATION, easing = TRANSITION_EASING),
                    initialOffsetX = { if (isNavigatingBack) -it else it },
                ),
                exit = slideOutHorizontally(
                    animationSpec = tween(PROGRESS_BAR_ANIM_DURATION, easing = TRANSITION_EASING),
                    targetOffsetX = { if (isNavigatingBack) it else -it },
                ),
            ) {
                Box(
                    modifier = Modifier.onSizeChanged { size ->
                        val newHeight = with(density) { size.height.toDp() }
                        if (newHeight != progressBarHeightDp) {
                            progressBarHeightDp = newHeight
                        }
                    },
                ) {
                    UScreenProgressBar(
                        modifier = Modifier.statusBarsPadding(),
                        state = activeChild.progressState,
                        canGoBack = false,
                        onBackClicked = component::onBackClicked,
                    )
                }
            }
        }
    }
}
